// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { formatBaht } from '@/lib/money'
import { FileText, Plus, Search, Lock } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Skeleton, Select, Pagination, Input, DataTable, CardKebabMenu, ConfirmDialog } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { ShowAllAffordance } from '@/components/ui/ShowAllAffordance'
import type { Column } from '@/components/ui/DataTable'
import { useListSort } from '@/hooks/useListSort'
import { useLeases, useCustomerLeases, useDeleteLease } from '@/hooks/useLeases'
import { useProperties } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useCustomers } from '@/hooks/useCustomers'
import { useSelectedPropertyStore } from '@/stores'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useAuth } from '@/hooks/useAuth'
import { useSetupScopeSession } from '@/hooks/useSupportSession'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Lease } from '@/types'

export function LeaseListPage() {
  const { t } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const { t: tCon } = useTranslation('contracts')
  // EP-59 V5 — condominium accounts list OwnerContract records; the
  // Rent column and draft/pending_signatures filter chips don't apply.
  const { data: accountSettings } = useAccountSettings()
  const formatDate = useDateFormatter()
  const isCondominium = accountSettings?.account_type === 'condominium'
  const navigate = useNavigate()
  // EP-129 D-3: the accountant reads leases but never changes them. Edit routes
  // to /leases/:id/edit, which AccountantBlockedRoute bounces to /, and Delete
  // 403s at PropertyPolicy#update? — so neither belongs in their row menu.
  // EP-160: a setup-scope support session may not create tenant data.
  const setupScope = useSetupScopeSession()
  const { user } = useAuth()
  const canMutateLease = user?.role !== 'accountant'
  const [searchParams] = useSearchParams()
  const customerIdParam = searchParams.get('customer_id')
  const customerIdFilter = customerIdParam ? Number(customerIdParam) : undefined
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const [pendingDelete, setPendingDelete] = useState<Lease | null>(null)
  const deleteLease = useDeleteLease()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('leases')

  // EP-97: rent-roll default view — LIVE leases (`''`) sorted by unit number asc.
  // The customer-scoped endpoint (`?customer_id=`) shows full history unsorted,
  // so the sort UI is suppressed there.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'unit_number',
    defaultOrder: 'asc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  // When filtering by customer_id, use the customer-scoped endpoint
  const { data: allLeasesData, isLoading: isLoadingAll, isFetching: isFetchingAll, error: allError } = useLeases(
    page,
    status || undefined,
    !customerIdFilter,
    searchQuery || undefined,
    selectedPropertyId ?? undefined,
    sortParams,
  )
  const { data: customerLeasesData, isLoading: isLoadingCustomer, isFetching: isFetchingCustomer, error: customerError } = useCustomerLeases(
    customerIdFilter ?? 0
  )

  const isLoading = customerIdFilter ? isLoadingCustomer : isLoadingAll
  const isFetching = customerIdFilter ? isFetchingCustomer : isFetchingAll
  const error = customerIdFilter ? customerError : allError

  // Normalise to the same shape for the template
  const leaseItems: Lease[] = customerIdFilter
    ? customerLeasesData ?? []
    : allLeasesData?.data ?? []
  const meta = customerIdFilter ? null : allLeasesData?.meta

  const { data: propertiesData } = useProperties(1)
  const { data: customersData } = useCustomers(1)
  const hasProperties = (propertiesData?.data?.length ?? 0) > 0
  const hasCustomers = (customersData?.data?.length ?? 0) > 0
  const allPrereqsMet = hasProperties && hasCustomers
  const properties = propertiesData?.data ?? []

  // EP-59 V5 — for condominium accounts, only active / ended apply
  // (OwnerContract state machine has no draft / pending_* states).
  // Rendering decision is DOM-level: the option entries are not present
  // when isCondominium, not just hidden via CSS.
  // EP-97: `''` is the LIVE default (server hides draft/ended); explicit `all`
  // is the escape that reveals every status.
  const statusOptions = isCondominium
    ? [
        { value: '', label: t('status.live_default') },
        { value: 'all', label: t('status.all') },
        { value: 'active', label: t('status.active') },
        { value: 'ended', label: t('status.ended') },
      ]
    : [
        { value: '', label: t('status.live_default') },
        { value: 'all', label: t('status.all') },
        { value: 'draft', label: t('status.draft') },
        { value: 'pending_signatures', label: t('status.pending_signatures') },
        { value: 'pending_start', label: t('status.pending_start') },
        { value: 'active', label: t('status.active') },
        { value: 'ended', label: t('status.ended') },
      ]

  const getStatusBadge = (s: string) => {
    switch (s) {
      case 'draft':
        return <Badge>{t('status.draft')}</Badge>
      case 'pending_signatures':
        return <Badge variant="warning">{t('status.pending_signatures')}</Badge>
      case 'pending_start':
        return <Badge variant="info">{t('status.pending_start')}</Badge>
      case 'active':
        return <Badge variant="success">{t('status.active')}</Badge>
      case 'ended':
        return <Badge variant="ghost">{t('status.ended')}</Badge>
      default:
        return <Badge>{s}</Badge>
    }
  }

  // EP-59 V5 — the Rent column is omitted entirely for condo accounts
  // (OwnerContract records have unit.price = 0 and no rent semantics).
  // DOM removal, not CSS hide.
  const columns: Column<Lease>[] = [
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      // 2026-09-29: dropped on a phone, where it pushed the status column off
      // the screen. A row still opens the lease, which has view, edit and
      // delete.
      hideBelow: 'sm',
      render: (l) => (
        <div onClick={(e) => e.stopPropagation()}>
          <CardKebabMenu
            ariaLabel={tc('labels.actions_menu')}
            items={[
              { label: tc('actions.view'), onClick: () => navigate(`/leases/${l.id}`) },
              ...(canMutateLease
                ? [
                    { label: tc('actions.edit'), onClick: () => navigate(`/leases/${l.id}/edit`) },
                    // T158-009: the server already refuses a non-draft lease
                    // (422 lease_not_draft); hide the action rather than let
                    // the operator hit a refusal the payload already shows.
                    // A draft lease WITH invoices is still offered here —
                    // that refusal (lease_has_invoices) isn't knowable from
                    // this payload, so it only surfaces via the toast below.
                    // An ownership contract has no draft state; deleting it is
                    // EP-134's undo for a wrong owner transfer (API allows it
                    // unless it has invoices).
                    ...(l.status === 'draft' || l.contract_type === 'ownership'
                      ? [{ label: tc('actions.delete'), variant: 'error' as const, onClick: () => setPendingDelete(l) }]
                      : []),
                  ]
                : []),
            ]}
          />
        </div>
      ),
    },
    // 2026-09-29: the unit leads; people find a lease by its room, not its
    // code. The code stays from `sm` up and is dropped on a phone.
    {
      key: 'unit_number',
      header: t('labels.unit'),
      sortable: !customerIdFilter,
      render: (l) => <span className="font-medium whitespace-nowrap">{l.unit?.name || '-'}</span>,
    },
    { key: 'customer', header: t('labels.customer'), render: (l) => l.customer?.name || '-' },
    { key: 'status', header: tc('labels.status'), render: (l) => getStatusBadge(l.status) },
    {
      key: 'lease_code',
      header: t('labels.lease_code'),
      hideBelow: 'sm',
      sortable: !customerIdFilter,
      render: (l) => <span className="whitespace-nowrap">{l.lease_code}</span>,
    },
    ...(isCondominium
      ? []
      : [
          {
            key: 'rent',
            header: t('labels.rent'),
            hideBelow: 'md',
            sortable: !customerIdFilter,
            render: (l: Lease) => (
              <span className="text-primary font-medium whitespace-nowrap">
                {formatBaht(l.rent_amount ?? l.unit?.price ?? 0)}
              </span>
            ),
          } as Column<Lease>,
        ]),
    {
      key: 'started_at',
      header: t('labels.start_date'),
      hideBelow: 'md',
      sortable: !customerIdFilter,
      render: (l) => <span className="whitespace-nowrap">{formatDate(l.started_at)}</span>,
    },
    {
      key: 'ended_at',
      header: t('labels.end_date'),
      hideBelow: 'lg',
      sortable: !customerIdFilter,
      render: (l) => (
        <span className="whitespace-nowrap">
          {l.ended_at ? formatDate(l.ended_at) : t('messages.ongoing')}
        </span>
      ),
    },
  ]

  const activeFilterCount = [status].filter(Boolean).length

  // EP-97: mobile sort options mirror the sortable columns (allow-listed on the
  // controller). Rent is omitted for condo accounts (no rent semantics).
  const sortOptions = [
    { value: 'unit_number', label: tc('sort.by_unit') },
    { value: 'lease_code', label: t('labels.lease_code') },
    { value: 'started_at', label: t('labels.start_date') },
    { value: 'ended_at', label: tc('sort.by_expiring') },
    ...(isCondominium ? [] : [{ value: 'rent', label: t('labels.rent') }]),
  ]

  return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: tc(isCondominium ? 'nav.co_ownerships' : 'nav.leases') }]} />

        <PageHeader
          title={isCondominium ? tCon('list.title.ownership') : t('title')}
          description={isCondominium ? undefined : t('description')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={[
                { value: '', label: tc('labels.all_properties') },
                ...properties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          // 2026-09-29: the shared header action, like Customers. On a phone it
          // joins the bottom action bar instead of sitting alone on its own row
          // above the deposit line.
          primaryAction={{
            key: 'add-lease',
            label: t('actions.add_lease'),
            icon: Plus,
            onClick: () => navigate('/leases/new'),
            hidden: setupScope,
          }}
        />

        {/* EP-149 T149-081 (MON-7 D): account-wide balance, server-computed
            by DepositsHeldSummary and returned as meta.deposits_held on this
            same list request. Absent on the customer-scoped endpoint. */}
        {meta?.deposits_held && (
          <div
            className="flex items-center gap-2 text-sm text-base-content/70"
            data-testid="leases-deposits-held"
          >
            <Lock className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>{t('deposits_held_summary.label')}</span>
            <span className="font-semibold text-base-content">
              {formatBaht(meta.deposits_held.total, { decimals: 0 })}
            </span>
          </div>
        )}

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('filters.search_placeholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
            />
          </div>

          {/* Mobile-only toggle */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="lease-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="lease-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile column sort lives inside the Filters disclosure;
                desktop uses the sortable DataTable headers instead. */}
            {!customerIdFilter && (
              <div className="w-full sm:hidden">
                <MobileSortControl
                  options={sortOptions}
                  value={sort}
                  order={order}
                  onChange={handleSort}
                  onToggleOrder={handleToggleOrder}
                />
              </div>
            )}
            <Select
              label={tc('labels.status')}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              options={statusOptions}
              className="w-full sm:w-48 min-w-0"
            />
          </div>
        </Card>

        {/* Lease List */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          </Card>
        ) : leaseItems.length === 0 ? (
          <Card>
            {!status && !customerIdFilter ? (
              <SetupGuide
                icon={<FileText className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prereq_property'),
                    met: hasProperties,
                    link: '/properties',
                    linkLabel: t('setup_guide.prereq_setup_property'),
                  },
                  {
                    label: t('setup_guide.prereq_customer'),
                    met: hasCustomers,
                    link: '/customers',
                    linkLabel: t('setup_guide.prereq_add_customer'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                  { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                ]}
                action={
                  setupScope ? undefined : (
                    <Button
                      leftIcon={<Plus className="w-4 h-4" />}
                      onClick={() => navigate('/leases/new')}
                      disabled={!allPrereqsMet}
                    >
                      {t('actions.add_lease')}
                    </Button>
                  )
                }
              />
            ) : (
              <EmptyState
                icon={<FileText className="w-12 h-12" />}
                title={t('empty.title')}
                description={t('empty.description')}
                action={
                  setupScope ? undefined : (
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/leases/new')}>
                      {t('actions.add_lease')}
                    </Button>
                  )
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            {/* EP-97: signal the active LIVE-leases default + one-click escape. */}
            {!status && !customerIdFilter && (
              <ShowAllAffordance
                label={t('status.showing_live')}
                onShowAll={() => { setStatus('all'); setPage(1) }}
              />
            )}
            <DataTable
              columns={columns}
              data={leaseItems}
              keyExtractor={(l) => l.id}
              onRowClick={(l) => navigate(`/leases/${l.id}`)}
              sortBy={customerIdFilter ? undefined : sort}
              sortOrder={customerIdFilter ? undefined : order}
              onSort={customerIdFilter ? undefined : handleSort}
            />
          </Card>
        )}

        {/* Pagination — only shown for unfiltered list */}
        {meta && meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={meta.current_page}
              totalPages={meta.total_pages}
              totalItems={meta.total_count}
              pageSize={meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            if (!pendingDelete) return
            try {
              await deleteLease.mutateAsync(pendingDelete.id)
              setPendingDelete(null)
            } catch (err) {
              addToast({ type: 'error', message: translateError(err) })
            }
          }}
          title={tc('actions.delete')}
          message={t('messages.delete_confirm')}
          confirmLabel={tc('actions.delete')}
          cancelLabel={tc('actions.cancel')}
          variant="error"
          loading={deleteLease.isPending}
        />
      </div>
  )
}
